import {
  Flex,
  Link,
  PrimaryHeading,
  PrimaryText,
  StyleProps,
  color,
  spacing,
  typography,
  useColorMode,
} from 'deepstash-ui';
import { useRouter } from 'next/router';
import useAuth from 'providers/hooks/useAuth';
import useSource from 'providers/hooks/useSource';
import React from 'react';
import CollectionCard from 'src/components/collection/CollectionCard';
import Layout from 'src/components/layout/Layout';
import { LayoutSection } from 'src/components/layout/Layout.types';
import FullWidthScrollableWrapper from 'src/components/layout/components/FullWidthScrollableWrapper';
import FullWidthSectionWrapper from 'src/components/layout/components/FullWidthSectionWrapper';
import SectionTitle from 'src/components/layout/components/SectionTitle';
import NextLink from 'src/components/navigation/NextLink';
import SourceCollapsed from 'src/components/source/source-collapsed/SourceCollapsed';
import { PageMetadata, SourceType } from 'types';
import { GAP_SIZE, GAP_SIZE_REM, SECTION_GAP_REM, slugify } from 'utils/global';
import CollectionInHeader from './CollectionInHeader';
import SimilarSourcesList from './SimilarSourcesList';
import SourceCardIdeas from './SourceCardIdeas';
import SourceCardMetaContent from './SourceCardIdeasMetaContent';
import SourceSplashscreenBackground from './SourceSplashscreenBackground';
import OtherTakesList from './OtherTakesList';
import { UNSORTED_TOPICS } from 'utils/metatopics.constants';

// const SourceDropdown = dynamic(() => import('./SourceDropdown'));

interface SourceCardProps {
  /**
   * Whether to have a static sourceCard that is not interactable
   */
  isNotInteractable?: boolean;
  pageMetadata: PageMetadata;
  sectionBefore?: LayoutSection;
}

// eslint-disable-next-line react/display-name
const SourceCard: React.FC<SourceCardProps & StyleProps> = ({
  pageMetadata,
  sectionBefore,
}) => {
  const { source } = useSource();
  const router = useRouter();

  const { colorMode } = useColorMode();
  const { isLoggedIn } = useAuth();

  const renderAuthorsSection = () => {
    if (
      source === undefined ||
      source.sourceType !== SourceType.BOOK ||
      source.seo?.source?.publishers === undefined ||
      source.seo?.source?.publishers?.length === 0
    ) {
      return null;
    }

    const publishersTotal = source.seo?.source.publishers.length;
    return (
      <Flex flexDirection="row" mt="0.5rem" gridColumnGap={8}>
        <PrimaryText
          type="regular"
          color={color[colorMode].textSecondary}
          size="l"
        >
          {'by'}
        </PrimaryText>
        {source.seo?.source.publishers.map((publisher, index) => {
          const publisherSlug = slugify(publisher.name);

          return (
            <NextLink
              href={`/book/author/${publisher.id}/${publisherSlug}`}
              passHref
              key={`publisher-${index}`}
            >
              <Link _focus={undefined} _hover={undefined}>
                <PrimaryText
                  type="semiBold"
                  color={color[colorMode].text}
                  size="l"
                >
                  {publisher.name}
                  {/** Add , commas between publishers */}
                  {publishersTotal > 1 && index < publishersTotal - 1 && ','}
                </PrimaryText>
              </Link>
            </NextLink>
          );
        })}
      </Flex>
    );
  };

  const authorsSectionComponent = React.useMemo(() => {
    return renderAuthorsSection();
  }, [source]);

  if (!source) return null;

  const sourceTitleSection: LayoutSection = {
    content: (
      <FullWidthSectionWrapper containerProps={{ flexDirection: 'column' }}>
        <SourceCollapsed
          showTopics
          size="full"
          py={GAP_SIZE_REM}
          preload={true}
          headingSize="h1"
          overrideAuthorComponent={authorsSectionComponent ?? undefined}
          shouldHideCta={true}
        />
      </FullWidthSectionWrapper>
    ),
    sectionStyle: {
      marginBottom: GAP_SIZE_REM,
    },
    isFullscreen: true,
  };

  const sourceIdeasSection: LayoutSection = {
    content: <SourceCardIdeas isNsiBlocked={!router.query.show} />,
    metaContent: <SourceCardMetaContent />,
    metaContentTopOffset: 0,
    metaContentStickyOffset: GAP_SIZE * 2,
    metaContentColumnStart: 0,
  };

  const recommendedCollectionsSection: LayoutSection = {
    content:
      !isLoggedIn &&
      !!source.seo?.similar_collections &&
      source.seo.similar_collections.length > 0 ? (
        <>
          {!!source.seo?.recommended_collection ? (
            <FullWidthSectionWrapper
              containerProps={{
                flexDirection: 'column',
                gridGap: spacing.M.rem,
              }}
              marginTop={GAP_SIZE_REM}
            >
              <PrimaryHeading
                size={'h3'}
                {...typography.primaryHeadingFonts.small.bold}
                color={color[colorMode].text}
              >
                {source?.userData?.firstName +
                  ' ' +
                  source?.userData?.lastName +
                  "'s"}{' '}
                ideas are part of this journey:
              </PrimaryHeading>
              <CollectionInHeader
                collection={source.seo.recommended_collection}
                topic={source.ideas[0].hashtags?.[0] ?? ''}
              />
            </FullWidthSectionWrapper>
          ) : null}
          <FullWidthSectionWrapper
            marginTop={{ base: GAP_SIZE_REM, md: SECTION_GAP_REM }}
          >
            <SectionTitle title={'Related collections'} />
          </FullWidthSectionWrapper>
          <FullWidthScrollableWrapper mt={32}>
            {source.seo?.similar_collections
              ?.slice(0, 4)
              .map((collection, index) => {
                return (
                  <CollectionCard
                    collection={collection}
                    key={`collection-${index}`}
                  />
                );
              })}
          </FullWidthScrollableWrapper>
        </>
      ) : null,
    isFullscreen: true,
  };

  const similarSourcesSection: LayoutSection = {
    content: (
      <FullWidthSectionWrapper
        mt={{ base: GAP_SIZE_REM, md: SECTION_GAP_REM }}
        containerProps={{ flexDirection: 'column' }}
      >
        {source.sourceType === SourceType.BOOK && <OtherTakesList />}
        <SimilarSourcesList />
      </FullWidthSectionWrapper>
    ),
    isFullscreen: true,
  };

  return (
    <>
      <SourceSplashscreenBackground
        zIndex={-1}
        themeColor={source.imageColor}
      />
      <Layout
        pageMetadata={pageMetadata}
        sections={
          sectionBefore
            ? [
                sectionBefore,
                sourceTitleSection,
                sourceIdeasSection,
                recommendedCollectionsSection,
                similarSourcesSection,
              ]
            : [
                sourceTitleSection,
                sourceIdeasSection,
                recommendedCollectionsSection,
                similarSourcesSection,
              ]
        }
        isShortProAd
        showFooterPrices={false}
        breadcrumbs={
          !!source.ideas[0]?.hashtags?.length
            ? [
                {
                  title: 'Home',
                  link: '/',
                },
                {
                  title:
                    UNSORTED_TOPICS[source.ideas[0]?.hashtags?.[0]]?.title ??
                    source.ideas[0]?.hashtags?.[0],
                  link: '/topic/' + source.ideas[0]?.hashtags?.[0],
                },
                {
                  title: source.title,
                  link: `/article/${source.id}/${slugify(source.title)}`,
                },
              ]
            : undefined
        }
      />
    </>
  );
};

export default SourceCard;
